import React, { useState, useEffect } from 'react';
import './CompletedProductionPage.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4001';

const CompletedProductionPage = () => {
    const [completedMeasurements, setCompletedMeasurements] = useState([]);
    const [filteredMeasurements, setFilteredMeasurements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [machineFilter, setMachineFilter] = useState('all');

    useEffect(() => {
        fetchCompletedMeasurements();
    }, []);

    useEffect(() => {
        filterMeasurements();
    }, [completedMeasurements, searchTerm, machineFilter]);

    const fetchCompletedMeasurements = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`${API_URL}/api/production/measurements/completed`);
            if (!response.ok) throw new Error('Failed to fetch completed measurements');
            const data = await response.json();
            setCompletedMeasurements(data);
        } catch (err) {
            console.error('Error fetching completed measurements:', err);
            setError('Failed to load completed measurements');
        } finally {
            setLoading(false);
        }
    };

    const filterMeasurements = () => {
        let filtered = [...completedMeasurements];

        // Filter by search term
        if (searchTerm) {
            filtered = filtered.filter(m =>
                m.wtNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (m.customerName && m.customerName.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (m.wtDescription && m.wtDescription.toLowerCase().includes(searchTerm.toLowerCase()))
            );
        }

        // Filter by machine (using actualMachineName)
        if (machineFilter !== 'all') {
            filtered = filtered.filter(m => m.actualMachineName === machineFilter);
        }

        setFilteredMeasurements(filtered);
    };

    const getUniqueMachines = () => {
        const machines = new Set(completedMeasurements.map(m => m.actualMachineName));
        return Array.from(machines).sort();
    };

    const getVarianceColor = (variancePercent) => {
        if (variancePercent === null || variancePercent === undefined) return '#999';
        if (variancePercent <= 0) return '#4CAF50'; // Green for under/on budget
        if (variancePercent <= 5) return '#FF9800'; // Orange for slight over
        return '#f44336'; // Red for significant over
    };

    const formatNumber = (num) => {
        if (num === null || num === undefined) return 'N/A';
        return parseFloat(num).toFixed(2);
    };

    return (
        <div className="completed-production-page">
            <div className="page-header">
                <h1>Completed Production Measurements</h1>
                <button className="refresh-button" onClick={fetchCompletedMeasurements}>
                    🔄 Refresh
                </button>
            </div>

            {error && <div className="error-message">{error}</div>}

            {/* Filters */}
            <div className="filters-section">
                <div className="search-box">
                    <input
                        type="text"
                        placeholder="Search by WT number, customer, or description..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <div className="machine-filter">
                    <label>Machine:</label>
                    <select value={machineFilter} onChange={(e) => setMachineFilter(e.target.value)}>
                        <option value="all">All Machines</option>
                        {getUniqueMachines().map(machine => (
                            <option key={machine} value={machine}>{machine}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Summary Stats */}
            <div className="summary-stats">
                <div className="stat-card">
                    <div className="stat-value">{filteredMeasurements.length}</div>
                    <div className="stat-label">Total Jobs</div>
                </div>
                <div className="stat-card">
                    <div className="stat-value">
                        {formatNumber(filteredMeasurements.reduce((sum, m) => sum + parseFloat(m.estimatedMeters || 0), 0))}m
                    </div>
                    <div className="stat-label">Total Estimated</div>
                </div>
                <div className="stat-card">
                    <div className="stat-value">
                        {formatNumber(filteredMeasurements.reduce((sum, m) => sum + parseFloat(m.totalActualMeters || 0), 0))}m
                    </div>
                    <div className="stat-label">Total Actual</div>
                </div>
                <div className="stat-card">
                    <div className="stat-value">
                        {formatNumber(filteredMeasurements.reduce((sum, m) => sum + parseFloat(m.variance || 0), 0))}m
                    </div>
                    <div className="stat-label">Total Variance</div>
                </div>
            </div>

            {/* Measurements Table */}
            {loading ? (
                <div className="loading">Loading completed measurements...</div>
            ) : filteredMeasurements.length === 0 ? (
                <div className="no-data">No completed measurements found</div>
            ) : (
                <div className="measurements-table-container">
                    <table className="measurements-table">
                        <thead>
                            <tr>
                                <th>WT Number</th>
                                <th>Customer</th>
                                <th>Operator</th>
                                <th>Machine</th>
                                <th>Meters Est</th>
                                <th>Meters Act</th>
                                <th>Meters Var</th>
                                <th>Width Est</th>
                                <th>Width Act</th>
                                <th>Across Est</th>
                                <th>Across Act</th>
                                <th>Completed</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredMeasurements.map(measurement => (
                                <tr key={measurement.id}>
                                    <td className="wt-number">
                                        {measurement.wtNumber}
                                        {measurement.machineChanged && (
                                            <span className="machine-changed-indicator" title="Machine changed from estimate">⚠️</span>
                                        )}
                                    </td>
                                    <td>{measurement.customerName || 'N/A'}</td>
                                    <td>{measurement.operator?.name || '-'}</td>
                                    <td>
                                        {measurement.actualMachineName}
                                        {measurement.machineChanged && (
                                            <div className="machine-note">Est: {measurement.estimatedMachineName}</div>
                                        )}
                                    </td>
                                    <td className="number">{formatNumber(measurement.estimatedMeters)}</td>
                                    <td className="number">{formatNumber(measurement.totalActualMeters)}</td>
                                    <td 
                                        className="number variance"
                                        style={{ color: getVarianceColor(measurement.variancePercent) }}
                                    >
                                        {formatNumber(measurement.variance)} ({formatNumber(measurement.variancePercent)}%)
                                    </td>
                                    <td className="number">{formatNumber(measurement.estimatedWidth)}</td>
                                    <td className="number">
                                        {formatNumber(measurement.actualWidth)}
                                        {measurement.widthVariance && (
                                            <span className="variance-small" style={{ color: measurement.widthVariance > 0 ? '#f44336' : '#4CAF50' }}>
                                                {measurement.widthVariance > 0 ? '+' : ''}{formatNumber(measurement.widthVariance)}
                                            </span>
                                        )}
                                    </td>
                                    <td className="number">{measurement.estimatedAcross || '-'}</td>
                                    <td className="number">
                                        {measurement.actualAcross || '-'}
                                        {measurement.acrossVariance && (
                                            <span className="variance-small" style={{ color: measurement.acrossVariance > 0 ? '#f44336' : '#4CAF50' }}>
                                                {measurement.acrossVariance > 0 ? '+' : ''}{measurement.acrossVariance}
                                            </span>
                                        )}
                                    </td>
                                    <td className="date">
                                        {new Date(measurement.completedAt).toLocaleDateString()}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default CompletedProductionPage;
